/**
 * RadarSpoke — one clickable spoke on the portfolio radar: hit-area, score /
 * in-progress badge, tip dot and label, linking into the lite assessment.
 */
import React from "react";
import { Link } from "react-router-dom";

export default function RadarSpoke({ s, active, onHover }) {
  // Compute a tight hit-area rectangle that bounds the dot + label.
  const labelWidth = Math.max(110, s.code.length * 9 + 12);
  const rectX =
    s.textAnchor === "start"
      ? s.labelPoint.x - 12
      : s.textAnchor === "end"
      ? s.labelPoint.x - labelWidth + 12
      : s.labelPoint.x - labelWidth / 2;
  const rectY = s.labelPoint.y - 22;
  return (
    <Link
      to={`/resources/${s.slug}-self-assessment`}
      aria-label={`Take the ${s.code} self-assessment`}
      data-testid={`portfolio-radar-spoke-${s.slug}`}
    >
      <g
        onMouseEnter={() => onHover(s.code)}
        onMouseLeave={() => onHover(null)}
        style={{ cursor: "pointer" }}
      >
        {/* Transparent rectangular hit-area covering dot → label. */}
        <rect
          x={Math.min(rectX, s.tip.x - 12)}
          y={Math.min(rectY, s.tip.y - 12)}
          width={Math.abs(rectX - s.tip.x) + labelWidth + 12}
          height={Math.max(48, Math.abs(rectY - s.tip.y) + 44)}
          fill="transparent"
        />
        {/* Score badge on completed spokes */}
        {s.snap?.overall ? (
          <g data-testid={`portfolio-radar-snap-${s.slug}`}>
            <rect x={s.tip.x - 18} y={s.tip.y + 11} width={36} height={16} rx={2} fill="#1E9E5A" />
            <text x={s.tip.x} y={s.tip.y + 22.5} textAnchor="middle" fontSize={10} fontWeight={700} fill="#FFFFFF" letterSpacing={0.4}>
              {s.snap.overall.toFixed(1)} / 4
            </text>
          </g>
        ) : s.draft ? (
          /* In-progress badge — amber pill with "X / Y" */
          <g data-testid={`portfolio-radar-draft-${s.slug}`}>
            <rect x={s.tip.x - 18} y={s.tip.y + 11} width={36} height={16} rx={2} fill="#E0A52A" />
            <text x={s.tip.x} y={s.tip.y + 22.5} textAnchor="middle" fontSize={10} fontWeight={700} fill="#0B0B0C" letterSpacing={0.4}>
              {s.draft.answered_count} / {s.draft.total_questions}
            </text>
          </g>
        ) : null}
        {/* Tip dot — green if completed, amber if in-progress, purple otherwise */}
        <circle
          cx={s.tip.x}
          cy={s.tip.y}
          r={active ? 9 : 6}
          fill={s.snap ? "#1E9E5A" : s.draft ? "#E0A52A" : active ? "#FFFFFF" : "#7737F6"}
          stroke="#FFFFFF"
          strokeWidth={active ? 2 : 1.5}
          style={{ transition: "all 180ms ease" }}
        />
        {/* Label */}
        <text
          x={s.labelPoint.x}
          y={s.labelPoint.y}
          textAnchor={s.textAnchor}
          dominantBaseline="middle"
          fontSize={13}
          fontWeight={active ? 700 : 600}
          letterSpacing={1.4}
          fill={active ? "#FFFFFF" : "#E9DEFE"}
          style={{ transition: "fill 180ms ease" }}
        >
          {s.code}
        </text>
        {/* Sub-label appears on hover */}
        {active ? (
          <text x={s.labelPoint.x} y={s.labelPoint.y + 16} textAnchor={s.textAnchor} dominantBaseline="middle" fontSize={10} fill="#A179FA" letterSpacing={1.6}>
            {s.draft && !s.snap ? "RESUME →" : "TAKE THE DIAGNOSTIC →"}
          </text>
        ) : null}
      </g>
    </Link>
  );
}
